'use client';

import { memo, useMemo } from 'react';

import { formatUnits } from 'viem';
import { type Address } from 'viem';
import { useChainId, useReadContract } from 'wagmi';

import { poolContractAbi } from '@/abi';
import USDCIcon from '@/assets/icons/usdc.svg';
import { Image } from '@/components/shared/Image';
import { P } from '@/components/shared/Typography';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { C_POOL_TOKEN_DECIMALS } from '@/ts/constants/C_DECIMAL_PLACES';
import { C_ACTIVE_NETWORK_ID } from '@/ts/constants/C_NETWORKS';
import { C_USDC_DECIMALS } from '@/ts/constants/C_USDC_DECIMALS';
import { T_Pool } from '@/ts/types/T_Pool';

interface I_TokenHeaderProps {
  bannerUrl: string;
  bannerAlt: string;
  tokenSymbol: string;
  currentPrice: number;
  color: string;
  t: (key: string) => string;
  pool?: T_Pool;
}

export const TokenHeader = memo(
  ({
    bannerUrl,
    bannerAlt,
    tokenSymbol,
    currentPrice,
    color,
    t,
    pool
  }: I_TokenHeaderProps) => {
    const chainId = useChainId();
    // Default to active network when wallet is not connected
    const activeChainId = chainId || C_ACTIVE_NETWORK_ID;

    // Get presaleTokenPriceDecimal and currentPriceDecimal from pool blockchain data
    const presaleTokenPriceDecimal =
      pool?.blockchainData?.presaleTokenPriceDecimal;
    const currentPriceDecimal = pool?.blockchainData?.currentPriceDecimal;

    const shouldFetchFromContract = useMemo(() => {
      if (!presaleTokenPriceDecimal || !currentPriceDecimal) {
        return false;
      }
      return presaleTokenPriceDecimal === currentPriceDecimal;
    }, [presaleTokenPriceDecimal, currentPriceDecimal]);

    const { data: contractTokenPrice } = useReadContract({
      address: pool?.poolAddress as Address | undefined,
      abi: poolContractAbi,
      functionName: 'getTokenPrice',
      chainId: activeChainId,
      query: {
        enabled: shouldFetchFromContract && !!pool?.poolAddress
      }
    }) as { data: bigint | undefined };

    const displayPrice = useMemo(() => {
      // If presaleTokenPriceDecimal === currentPriceDecimal, use contract price
      if (shouldFetchFromContract) {
        if (contractTokenPrice !== undefined) {
          // Convert bigint to decimal string using USDC decimals (6)
          return formatUnits(contractTokenPrice, C_USDC_DECIMALS);
        }
        // If contract call is still loading or failed, fallback to currentPriceDecimal
        if (currentPriceDecimal) {
          return currentPriceDecimal;
        }
      }

      // If not equal, use currentPriceDecimal from pool blockchain data
      if (!shouldFetchFromContract && currentPriceDecimal) {
        return currentPriceDecimal;
      }

      // Fallback to passed currentPrice prop
      return currentPrice.toString();
    }, [
      shouldFetchFromContract,
      contractTokenPrice,
      currentPriceDecimal,
      currentPrice
    ]);

    return (
      <div className="px-7 py-5 border-b border-base-border/20 flex justify-start items-center">
        <div className="flex align-center gap-4 w-full justify-between overflow-x-auto">
          <div className="flex items-center">
            <div className="relative w-12 h-12 mr-4">
              <Image
                className="w-full"
                width={48}
                height={48}
                src={bannerUrl}
                alt={bannerAlt}
              />
            </div>
            <div className="flex flex-col">
              <P className="text-xl font-bold">{tokenSymbol}</P>
            </div>
          </div>
          <div className="flex flex-col items-end justify-between">
            <P className="text-sm pt-1 font-medium ">{t('tokenPrice')}</P>
            <P
              className="text-xl font-bold "
              style={{ color }}
              icon={<USDCIcon />}
            >
              {formatNumberWithCommasAndDecimals(
                displayPrice,
                C_POOL_TOKEN_DECIMALS
              )}
            </P>
          </div>
        </div>
      </div>
    );
  }
);

TokenHeader.displayName = 'TokenHeader';
